import React from 'react';
import { Box, Typography, Link as MuiLink } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Product } from '../../types';
import ProductCard from '../common/ProductCard';

interface ProductRailProps {
  title: string;
  subtitle?: string;
  products: Product[];
  viewAllHref?: string;
  onAddToCart: (product: Product) => void;
  onAddToWishlist: (product: Product) => void;
}

const ProductRail: React.FC<ProductRailProps> = ({
  title,
  subtitle,
  products,
  viewAllHref,
  onAddToCart,
  onAddToWishlist
}) => {
  if (!products.length) return null;

  return (
    <Box sx={{ mb: { xs: 5, md: 7 } }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          flexDirection: 'row',
          justifyContent: 'space-between',
          mb: 2.25,
          gap: 1.5,
          flexWrap: 'wrap'
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ mb: 0.35, fontSize: { xs: '1.85rem', sm: '2rem' } }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        {viewAllHref && (
          <MuiLink
            component={RouterLink}
            to={viewAllHref}
            underline="none"
            sx={{
              fontSize: '0.9rem',
              color: 'primary.main',
              whiteSpace: 'nowrap',
              fontWeight: 600,
              marginLeft: 'auto',
              '&:hover': { color: 'primary.dark' }
            }}
          >
            View all
          </MuiLink>
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          gap: 2.25,
          overflowX: 'auto',
          px: { xs: 0, sm: 0.35 },
          pb: 2,
          pr: { xs: 0.5, sm: 2 },
          scrollSnapType: 'x proximity',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' }
        }}
      >
        {products.map((product) => (
          <Box
            key={product._id}
            sx={{
              flex: { xs: '0 0 78vw', sm: '0 0 248px', md: '0 0 280px' },
              maxWidth: { xs: 296, sm: 'none' },
              scrollSnapAlign: 'start'
            }}
          >
            <ProductCard
              product={product}
              onAddToCart={onAddToCart}
              onAddToWishlist={onAddToWishlist}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default ProductRail;

